<template>
  <OnboardingTour v-if="showOnboarding" @done="dismissOnboarding" />
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 sm:py-8 px-4">
      <div class="max-w-4xl mx-auto">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold">Speak Saigon</h1>
            <p class="mt-1 sm:mt-2 text-sm sm:text-base text-emerald-100">{{ dialect === 'north' ? 'Learn Vietnamese · Hanoi accent (beta)' : 'Learn Southern Vietnamese' }}</p>
          </div>
          <div class="flex flex-col items-start gap-2.5 sm:items-end">
            <!-- Chip labels collapse to their icons on mobile so this stays one
                 row instead of wrapping into a ragged block. -->
            <div class="flex flex-wrap items-center gap-2">
              <div
                class="relative inline-flex items-center rounded-full bg-white p-1 shadow-sm"
                title="Accent for audio and words — Southern (Saigon) or Northern (Hanoi)"
              >
                <button
                  v-for="d in dialectOptions"
                  :key="d.id"
                  @click="setDialect(d.id)"
                  class="px-2.5 sm:px-3 py-1 rounded-full text-xs font-bold transition-colors"
                  :class="dialect === d.id ? 'bg-emerald-600 text-white' : 'text-emerald-700 hover:bg-emerald-50'"
                >
                  {{ d.label }}
                </button>
                <span class="absolute -top-2 -right-1 bg-amber-400 text-amber-900 text-[9px] font-extrabold px-1.5 py-px rounded-full uppercase tracking-wide shadow-sm pointer-events-none">Beta</span>
              </div>
              <DarkModeToggle />
              <a
                v-if="!loading && dueCount > 0"
                href="/review/vocab"
                class="inline-flex items-center gap-1 bg-amber-400 text-amber-900 text-xs font-semibold px-2.5 sm:px-3 py-1.5 sm:py-1 rounded-full hover:bg-amber-300 transition-colors"
                :title="`${dueCount} ${dueCount === 1 ? 'word' : 'words'} due for review`"
              >&#127793; {{ dueCount }}<span class="hidden sm:inline"> due</span></a>
              <!-- The empty state is pure reassurance, so it stays off the
                   cramped mobile row entirely. -->
              <span
                v-else-if="!loading"
                class="hidden sm:inline-flex items-center gap-1 bg-emerald-500/40 text-emerald-50 text-xs font-semibold px-3 py-1 rounded-full"
                title="No vocabulary due for review"
              >&#10003; No reviews due</span>
              <a
                v-if="!loading && mistakesCount > 0"
                href="/review/mistakes"
                class="inline-flex items-center gap-1 bg-rose-100 text-rose-700 text-xs font-semibold px-2.5 sm:px-3 py-1.5 sm:py-1 rounded-full hover:bg-rose-200 transition-colors"
                :title="`${mistakesCount} ${mistakesCount === 1 ? 'mistake' : 'mistakes'} to fix`"
              >&#129657; {{ mistakesCount }}<span class="hidden sm:inline"> to fix</span></a>
              <a
                href="/progress"
                class="inline-flex items-center gap-1 bg-emerald-500/40 text-white text-xs font-semibold px-2.5 sm:px-3 py-1.5 sm:py-1 rounded-full hover:bg-emerald-500/60 transition-colors"
                title="View your progress"
              >&#128202;<span class="hidden sm:inline"> Progress</span></a>
              <a
                href="/settings"
                class="inline-flex items-center gap-1 bg-emerald-500/40 text-white text-xs font-semibold px-2.5 sm:px-3 py-1.5 sm:py-1 rounded-full hover:bg-emerald-500/60 transition-colors"
                title="Settings"
              >&#9881;</a>
              <a
                v-if="subscribed"
                :href="billingUrl"
                class="inline-flex items-center gap-1 bg-emerald-500/40 text-white text-xs font-semibold px-2.5 sm:px-3 py-1.5 sm:py-1 rounded-full hover:bg-emerald-500/60 transition-colors"
                title="Manage your subscription"
              >&#11088;<span class="hidden sm:inline"> Pro</span></a>
              <a
                v-else
                :href="billingUrl"
                class="inline-flex items-center gap-1 bg-amber-400 text-amber-900 text-xs font-semibold px-2.5 sm:px-3 py-1.5 sm:py-1 rounded-full hover:bg-amber-300 transition-colors"
              >&#128274; Upgrade</a>
            </div>
            <!-- Stats on their own row — inside the chip row they force an
                 awkward mid-row wrap on smaller headers. -->
            <StatsBar />
            <button
              @click="signOut"
              class="text-xs text-emerald-300 hover:text-white transition-colors"
            >
              Sign out ({{ userEmail }})
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="max-w-4xl mx-auto px-4 py-8">
      <!-- Continue learning: ONE clear next action, everything else secondary. -->
      <div
        v-if="!loading && continueStep"
        class="mb-6 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-5 sm:p-6 text-white shadow-md"
      >
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div class="min-w-0">
            <p class="text-xs font-semibold uppercase tracking-wide text-emerald-100">{{ continueStep.kicker }}</p>
            <h2 class="mt-1 text-xl font-bold truncate">{{ continueStep.title }}</h2>
            <p class="mt-0.5 text-sm text-emerald-100">{{ continueStep.sub }}</p>
          </div>
          <a
            :href="continueStep.href"
            class="shrink-0 inline-flex items-center justify-center bg-white text-emerald-700 font-bold px-6 py-3 rounded-xl shadow-sm hover:bg-emerald-50 transition-colors"
          >
            {{ continueStep.cta }} &rarr;
          </a>
        </div>
        <p v-if="nextLesson || alsoDue || mistakesCount > 0" class="mt-3 text-xs text-emerald-100">
          <template v-if="nextLesson">
            Next lesson:
            <a :href="`/units/${nextLesson.id}`" class="underline hover:text-white font-medium">Unit {{ nextLesson.position }} · {{ nextLesson.title }} &rarr;</a>
          </template>
          <template v-if="nextLesson && (alsoDue || mistakesCount > 0)"> &middot; </template>
          <template v-if="alsoDue || mistakesCount > 0">
            Also waiting:
            <a v-if="alsoDue" href="/review/vocab" class="underline hover:text-white">&#127793; {{ dueCount }} words due</a>
            <template v-if="alsoDue && mistakesCount > 0"> · </template>
            <a v-if="mistakesCount > 0" href="/review/mistakes" class="underline hover:text-white">&#129657; {{ mistakesCount }} mistakes to fix</a>
          </template>
        </p>
      </div>

      <a v-if="dueCount > 0 && continueStep?.kind !== 'review'"
         href="/review/vocab"
         class="mb-6 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-4 hover:bg-amber-100 transition-colors">
        <div class="flex items-center gap-3">
          <span class="text-2xl">&#127793;</span>
          <div>
            <p class="font-semibold text-amber-900">{{ dueCount }} words to review</p>
            <p class="text-sm text-amber-700">Keep your vocabulary fresh</p>
          </div>
        </div>
        <span class="text-amber-600 font-medium text-sm">Review now &rarr;</span>
      </a>

      <div class="mb-8 space-y-5">
        <section v-for="group in linkGroups" :key="group.title">
          <h2 class="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
            {{ group.title }}
          </h2>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <a
              v-for="link in group.links" :key="link.href"
              :href="isLocked(link) ? billingUrl : link.href"
              @click="isLocked(link) && onLockedLink($event, link)"
              class="relative flex flex-col items-center justify-center text-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-500 transition-all"
            >
              <span
                v-if="isLocked(link)"
                class="absolute top-2 right-2 text-xs"
                title="Pro feature — upgrade to unlock"
              >&#128274;</span>
              <span class="text-2xl">{{ link.icon }}</span>
              <span class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ link.label }}</span>
              <span v-if="link.sub" class="text-[11px] leading-tight text-gray-500 dark:text-gray-400">{{ link.sub }}</span>
            </a>
          </div>
        </section>
      </div>

      <div v-if="loading" class="text-center py-12 text-gray-500">Loading courses...</div>
      <div v-else-if="error" class="text-center py-12">
        <p class="text-red-500">{{ error }}</p>
        <button
          type="button" @click="loadCourses"
          class="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >Try again</button>
      </div>
      <div v-else>
        <div v-for="level in levels" :key="level" class="mb-10">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-100">{{ levelLabel(level) }}</h2>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <UnitCard
              v-for="unit in unitsByLevel(level)"
              :key="unit.id"
              :unit="unit"
              :progress="progress[unit.id]"
              :recommended="recommendedUnit?.id === unit.id"
              :jump-ahead="isJumpAhead(unit)"
              @jump="jumpTarget = $event"
            />
          </div>

          <!-- The story and the checkpoint come AFTER the units, because that is
               when to do them: the checkpoint is cumulative over the whole level
               and the story reuses its vocabulary. Sitting in the level header
               they read as alternatives to the units rather than the thing that
               closes the level out. -->
          <div
            v-if="level > 0"
            class="mt-4 rounded-xl border-2 border-dashed p-4 flex flex-wrap items-center justify-between gap-3"
            :class="checkpointPassed(level)
              ? 'border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/10 dark:border-emerald-800'
              : 'border-gray-200 bg-gray-50/80 dark:bg-gray-800/40 dark:border-gray-700'"
          >
            <div class="min-w-0">
              <p class="text-sm font-semibold"
                 :class="checkpointPassed(level)
                   ? 'text-emerald-900 dark:text-emerald-200'
                   : 'text-gray-800 dark:text-gray-100'">
                <template v-if="checkpointPassed(level)">&#127941; Level {{ level }} complete</template>
                <template v-else>Before moving on</template>
              </p>
              <p class="text-xs mt-0.5"
                 :class="checkpointPassed(level) ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500 dark:text-gray-400'">
                <template v-if="checkpointPassed(level)">
                  Best {{ checkpoints[level].best_pct }}% &mdash; revisit any time.
                </template>
                <template v-else>
                  Read the story, hear a conversation, then pass the checkpoint (80%) to lock this level in.
                </template>
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <a
                :href="`/levels/${level}/story`"
                @click="onLevelAction($event, level, 'Stories')"
                class="text-sm bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-medium hover:bg-purple-200 transition-colors"
              >
                Read a Story
              </a>
              <a
                :href="`/conversations/scripted?level=${level}`"
                @click="onLevelAction($event, level, 'Scripted conversations')"
                class="text-sm bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700 transition-colors"
              >
                Hear a Conversation
              </a>
              <a
                :href="`/levels/${level}/review`"
                @click="onLevelAction($event, level, `The Level ${level} checkpoint`)"
                class="text-sm px-4 py-2 rounded-lg font-medium transition-colors"
                :class="checkpointPassed(level)
                  ? 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'"
                :title="checkpointPassed(level) ? `Checkpoint passed · Best ${checkpoints[level].best_pct}%` : 'Cumulative checkpoint — 80% passes'"
              >
                <template v-if="checkpointPassed(level)">Retake checkpoint</template>
                <template v-else>Checkpoint: Level {{ level }}</template>
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Jump-ahead confirm: a soft rail, not a lock — one tap continues anyway. -->
    <div
      v-if="jumpTarget"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      @click.self="jumpTarget = null"
    >
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-6">
        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">Skipping ahead?</h3>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          <template v-if="recommendedUnit">
            <span class="font-semibold">Unit {{ recommendedUnit.position }} · {{ recommendedUnit.title }}</span>
            is your next step — each unit builds on the last.
          </template>
          <template v-else>Each unit builds on the last.</template>
        </p>
        <div class="mt-5 flex flex-col gap-2">
          <a
            v-if="recommendedUnit"
            :href="`/units/${recommendedUnit.id}`"
            class="w-full text-center bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
          >
            Go to Unit {{ recommendedUnit.position }}
          </a>
          <a
            :href="`/units/${jumpTarget.id}`"
            class="w-full text-center bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 px-4 py-2.5 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Jump to Unit {{ jumpTarget.position }} anyway
          </a>
        </div>
      </div>
    </div>
  </div>

  <FeedbackFooter />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet, apiPost } from '../api/client'
import { showUpsell } from '../utils/upsell'
import { track } from '../utils/analytics'
import UnitCard from './UnitCard.vue'
import StatsBar from './shared/StatsBar.vue'
import DarkModeToggle from './shared/DarkModeToggle.vue'
import OnboardingTour from './OnboardingTour.vue'
import FeedbackFooter from './shared/FeedbackFooter.vue'

// The tour used to be remembered in localStorage alone, so it re-fired on
// every new device a learner signed in from. The account is now the source of
// truth; localStorage stays as a same-device fast path so the tour doesn't
// flash before the prefs request lands.
const ONBOARDING_KEY = 'speaksaigon_onboarded'
const showOnboarding = ref(false)

function dismissOnboarding() {
  showOnboarding.value = false
  try { localStorage.setItem(ONBOARDING_KEY, '1') } catch (e) { /* ignore */ }
  apiPost('/api/v1/account_prefs', { onboarded: true }).catch(() => {})
}

const props = defineProps({
  unitsUrl: { type: String, required: true },
  signOutUrl: { type: String, required: true },
  userEmail: { type: String, required: true },
  billingUrl: { type: String, default: '/billing' },
  subscribed: { type: Boolean, default: false },
  dialect: { type: String, default: 'south' },
})

// Account-wide accent preference: alphabet, tone trainer, and (eventually)
// lesson audio read it. Saved server-side — it must follow the account, not
// the device. Optimistic with rollback, same pattern as the email prefs.
const dialectOptions = [
  { id: 'south', label: 'Saigon' },
  { id: 'north', label: 'Hanoi' },
]
const dialect = ref(props.dialect)

async function setDialect(id) {
  if (dialect.value === id) return
  const previous = dialect.value
  dialect.value = id
  try {
    await apiPost('/api/v1/dialect', { dialect: id })
    track('dialect_preference_changed', { dialect: id })
  } catch (_e) {
    dialect.value = previous
  }
}

const units = ref([])
const progress = ref({})
const dueCount = ref(0)
const mistakesCount = ref(0)
const checkpoints = ref({})
const loading = ref(true)
const error = ref(null)
const jumpTarget = ref(null)

const levels = computed(() => {
  const lvls = [...new Set(units.value.map(u => u.level))]
  return lvls.sort((a, b) => a - b)
})

function unitsByLevel(level) {
  return units.value.filter(u => u.level === level).sort((a, b) => a.position - b.position)
}

function levelLabel(level) {
  return level === 0 ? 'Start Here · Foundations' : `Level ${level}`
}

function checkpointPassed(level) {
  return !!checkpoints.value[level]?.passed
}

// --- The course spine: one recommended next action ---

const orderedUnits = computed(() =>
  [...units.value].sort((a, b) => a.position - b.position)
)

function started(unit) {
  const p = progress.value[unit.id]
  return !!p && (p.learned || (p.exercises_completed || 0) > 0)
}

// The first started-but-not-completed unit (in course order): work in progress.
const currentUnit = computed(() =>
  orderedUnits.value.find(u => !u.locked && started(u) && !progress.value[u.id]?.completed)
)

// The first unit that isn't completed at all: where a fresh path continues.
const nextUnit = computed(() =>
  orderedUnits.value.find(u => !u.locked && !progress.value[u.id]?.completed)
)

const recommendedUnit = computed(() => currentUnit.value || nextUnit.value)

// Unstarted units past the recommendation get a soft "skipping ahead?" rail.
function isJumpAhead(unit) {
  if (!recommendedUnit.value || unit.locked) return false
  return unit.position > recommendedUnit.value.position && !started(unit) && !progress.value[unit.id]?.completed
}

const continueStep = computed(() => {
  const u = currentUnit.value
  if (u) {
    const p = progress.value[u.id]
    return p?.learned
      ? { kind: 'unit', kicker: 'Continue learning', title: `Unit ${u.position} · ${u.title}`, sub: 'Practice to complete this unit (70%+ does it)', href: `/units/${u.id}/practice`, cta: 'Practice' }
      : { kind: 'unit', kicker: 'Continue learning', title: `Unit ${u.position} · ${u.title}`, sub: 'Finish the lesson walkthrough', href: `/units/${u.id}`, cta: 'Continue' }
  }
  if (dueCount.value > 0) {
    return { kind: 'review', kicker: 'Keep it fresh', title: `${dueCount.value} words due for review`, sub: 'A quick review locks in what you learned', href: '/review/vocab', cta: 'Review' }
  }
  const n = nextUnit.value
  if (n) {
    return { kind: 'unit', kicker: 'Up next', title: `Unit ${n.position} · ${n.title}`, sub: n.description || 'Start the next unit on your path', href: `/units/${n.id}`, cta: 'Start' }
  }
  return { kind: 'review', kicker: 'All caught up', title: 'Everything is complete 🎉', sub: 'Run a quick review to keep it sharp', href: '/review/vocab?mode=quick', cta: 'Quick review' }
})

// Don't repeat the hero's own suggestion in its "Also waiting" footnote: when
// the main CTA already points at the due reviews, only mistakes remain "also".
const alsoDue = computed(() => dueCount.value > 0 && continueStep.value?.kind !== 'review')

// When the hero leads with reviews, keep the course path one click away by
// naming the next lesson in the footnote instead.
const nextLesson = computed(() =>
  continueStep.value?.kind === 'review' && dueCount.value > 0 ? nextUnit.value : null
)

// Locked quick-links (Games, Tone Lab, AI Conversation) pointed straight at
// /billing, so the paywall moment had no context. The href stays for
// middle-click and no-JS; the handler upgrades it to the in-place modal.
function onLockedLink(event, link) {
  event.preventDefault()
  showUpsell({ unitTitle: link.label })
}

const quickLinks = [
  // Grouped because a flat grid of six unlabelled tiles gave no clue which were
  // for consolidating what you know and which were for stretching. "Alphabet &
  // Tones" is gone: unit 0 sits directly below as its own card, so the tile was
  // a duplicate route to the same place.
  { group: 'Review', href: '/review/vocab?mode=quick', icon: '📚', label: 'Quick Review' },
  { group: 'Review', href: '/review/deck', icon: '🔖', label: 'My Deck' },
  // Ungated on purpose. The only two links to the mistakes bank were both
  // hidden when the count was zero, and the copy explaining the feature only
  // rendered on an empty state you could not reach — so nobody ever found it.
  // A zero state here reads as a reward; an absent tile reads as a secret.
  { group: 'Review', href: '/review/mistakes', icon: '🎯', label: 'Mistakes',
    sub: 'Replay what you got wrong' },
  { group: 'Practice', href: '/games', icon: '🎮', label: 'Games' },
  // "Tone Lab" for the members tool: the public /tone-trainer keeps its name
  // (it is the indexed URL with the traffic), so the two stop colliding.
  { group: 'Practice', href: '/tone-practice', icon: '🎤', label: 'Tone Lab' },
  { group: 'Practice', href: '/conversations', icon: '💬', label: 'AI Conversation', pro: true },
]

// Preserve declaration order within each group, and don't hard-code the group
// list — adding a link with a new `group` should just work.
const linkGroups = computed(() => {
  const out = []
  for (const link of quickLinks) {
    let g = out.find(x => x.title === link.group)
    if (!g) { g = { title: link.group, links: [] }; out.push(g) }
    g.links.push(link)
  }
  return out
})

// Pro-only quick links route a free user to the upgrade page and show a lock badge.
function isLocked(link) {
  return link.pro && !props.subscribed
}

// Mirrors the server's require_subscription_for_level: a level is gated when any
// unit up to it is Pro. Without this the level band happily navigated to a page
// that 402'd on load, which showed the upsell on top of a broken screen.
function levelLocked(level) {
  if (props.subscribed) return false
  return units.value.some(u => u.level > 0 && u.level <= level && u.locked)
}

function onLevelAction(event, level, label) {
  if (!levelLocked(level)) return
  event.preventDefault()
  showUpsell({ level, unitTitle: label })
}

async function signOut() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  const csrf = meta ? meta.getAttribute('content') : ''
  await fetch(props.signOutUrl, { method: 'DELETE', headers: { 'X-CSRF-Token': csrf } })
  window.location.href = '/session/new'
}

onMounted(async () => {
  let seenHere = true
  try { seenHere = !!localStorage.getItem(ONBOARDING_KEY) } catch (e) { /* ignore */ }
  if (!seenHere) {
    // Ask the account before showing it — a learner who dismissed the tour on
    // their phone should not meet it again on their laptop.
    apiGet('/api/v1/account_prefs')
      .then(prefs => {
        if (prefs?.onboarded) {
          try { localStorage.setItem(ONBOARDING_KEY, '1') } catch (e) { /* ignore */ }
        } else {
          showOnboarding.value = true
        }
      })
      .catch(() => { showOnboarding.value = true })
  }

  await loadCourses()
})

// Named so the error state can offer a real retry. A transient fetch failure on
// the PRIMARY surface used to leave "Failed to load courses" and no way out
// except a manual page reload.
async function loadCourses() {
  loading.value = true
  error.value = null
  try {
    const [unitsData, progressData, dueData, mistakesData, checkpointData] = await Promise.all([
      apiGet(props.unitsUrl),
      apiGet('/api/v1/progress').catch(() => []),
      apiGet('/api/v1/vocab_reviews/due_count').catch(() => ({ due_count: 0 })),
      apiGet('/api/v1/mistakes/count').catch(() => ({ count: 0 })),
      apiGet('/api/v1/progress/checkpoints').catch(() => []),
    ])
    units.value = unitsData
    progressData.forEach(p => {
      progress.value[p.unit_id] = p
    })
    dueCount.value = dueData.due_count
    mistakesCount.value = mistakesData.count
    checkpointData.forEach(c => {
      checkpoints.value[c.level] = c
    })
  } catch (e) {
    error.value = 'We couldn\u2019t load your courses.'
  } finally {
    loading.value = false
  }
}
</script>
